import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  STORAGE,
  getLocale,
  t,
  getRevealStyle,
  getDefaultTitle,
  readSwiperCard,
  formatSwiperCard,
  getSwiperConfig,
  loadDecoys,
  saveDecoys,
  updateDecoyById,
  isCompactReveal,
  abbreviateReveal,
  getAddNumberConfig,
  parseNumberedLines,
  replaceNumberOnLine,
  computeAddNumberSwap,
  isAddNumberReady,
  getTridentConfig,
  parseTridentWordLines,
  buildTridentReveal,
  shareNoteAsAirDrop,
  hapticTap,
} from "@/lib/mr";
import useReveal from "@/hooks/useReveal";
import useTypewriter from "@/hooks/useTypewriter";
import { toast } from "sonner";

// Number of blank lines we prepend before the answer in Scroll-peek mode so
// the writing lands comfortably below the spectator-visible viewport.
const SCROLL_PEEK_PADDING_LINES = 32;

/**
 * Performance screen disguised as Apple Notes.
 *
 * The behaviour is decided entirely by the `entry` field on the router
 * state passed by DecoyNotes:
 *
 *   • "swiper"  → top decoy. Body is pre-seeded with the captured
 *                 clock-swipe prediction (card or custom word). No input
 *                 mode, no reveal — the answer IS the body.
 *   • "compose" → + button. Opens a blank Type-or-Draw input note with
 *                 multi-question parsing (1: / 2: / 3:) always on.
 *   • "plain"   → any other row. Blank Type-or-Draw note, no multi-question.
 *
 * Gestures (compose/plain only):
 *   - Triple-tap date line → exit to setup
 *   - Long-press date line → silent recompute (forces a re-roll)
 *   - Tap ellipsis (•••)    → reveal answer (or hide it again)
 */
export default function Trick() {
  const navigate = useNavigate();
  const location = useLocation();

  // Router state from DecoyNotes — see file header for the four cases.
  const entry = (location.state && typeof location.state.entry === "string")
    ? location.state.entry
    : "plain";
  const entryTitle = (location.state && typeof location.state.entryTitle === "string")
    ? location.state.entryTitle
    : null;
  // Magician-configured datetime for the entry row, if any. Wins over
  // backdate / current-time fallbacks.
  const entryTime = (location.state && typeof location.state.entryTime === "string")
    ? location.state.entryTime
    : null;
  // Pre-rendered body + hardcoded date label for the Search-Force
  // prediction note. Both bypass the normal date/backdate logic so the
  // note reads exactly as the magician configured it.
  const entryBody = (location.state && typeof location.state.entryBody === "string")
    ? location.state.entryBody
    : null;
  const entryDateLabel = (location.state && typeof location.state.entryDateLabel === "string")
    ? location.state.entryDateLabel
    : null;
  // Stable decoy id (UUID) — used to hydrate / persist this note's
  // body & title across navigation so the spectator (and magician)
  // sees their previous input on reopen until it's overwritten.
  const entryId = (location.state && typeof location.state.entryId === "string")
    ? location.state.entryId
    : null;

  // --- Settings snapshot (lazy from localStorage to avoid race) ---
  // Swiper entries render a static body and ignore textarea input.
  // Drawing mode was retired — the pencil icon is now the secret
  // arming toggle for the Add-a-Number routine (see addnum* state below).
  const [langCode] = useState(() => localStorage.getItem(STORAGE.LANGUAGE) || "auto");
  const [showHint] = useState(() => {
    const h = localStorage.getItem(STORAGE.HINT);
    return h === null ? true : h === "1";
  });
  const [predictionPrefix] = useState(
    () => localStorage.getItem(STORAGE.PREDICTION_PREFIX) || ""
  );
  const [revealStyle] = useState(() => getRevealStyle());
  const [compactRevealOn] = useState(() => isCompactReveal());
  const [backdateMin] = useState(() => {
    const v = parseInt(localStorage.getItem(STORAGE.BACKDATE_MIN) || "0", 10);
    return Number.isFinite(v) && v >= 0 ? Math.min(v, 180) : 0;
  });

  // Multi-question is now locked to the + compose entry — see Settings
  // restructure notes. useReveal consumes this directly via prop.
  const multiQuestion = entry === "compose";

  // --- Note content (lazy init + autosave) ---
  // For swiper entries the body is synthesised synchronously from
  // localStorage transients + the matching decoy row's preview, so the
  // open note reads as pre-written. For compose/plain entries we fall
  // back to the magician's "default title" + persisted scratch body.
  const [predictionSeed] = useState(() => {
    const decoys = loadDecoys();
    const top = decoys[0] || {};

    // --- Search-Force fake prediction ---
    // Title + body are fully synthesised in DecoyNotes.openSearchForce
    // and passed through router state. Nothing to look up, no sticky
    // persistence — this note only exists for the duration of the tap.
    if (entry === "searchforce") {
      return {
        title: entryTitle || "Prediction",
        body: entryBody || "",
      };
    }

    // --- Trident second-row decoy ---
    // Always start with a clean blank body. The magician types "1./2./3."
    // themselves on stage; persisted scratch from a previous performance
    // must NOT bleed in here (would re-display old spectator words).
    if (entry === "trident") {
      return { title: entryTitle || getDefaultTitle(), body: "" };
    }

    // --- Swiper top row ---
    if (entry === "swiper") {
      const card = readSwiperCard();
      const swiper = getSwiperConfig();
      if (card && card.value && swiper.enabled) {
        const formatted = formatSwiperCard(card, swiper.format, swiper.template, langCode);
        if (formatted) {
          const prefix = (predictionPrefix || "").trim();
          const cardLine = prefix ? `${prefix} ${formatted}` : formatted;
          const usingCustomTemplate =
            (swiper.mode === "custom" && (swiper.template || "").includes("(xxx)")) ||
            (swiper.format === "custom" && (swiper.template || "").includes("(xxx)"));
          const preview = (top.preview || "").trim();
          const body = (usingCustomTemplate || !preview) ? cardLine : `${preview}\n\n${cardLine}`;
          const seedTitle = (entryTitle !== null ? entryTitle : top.title) || getDefaultTitle();
          return { title: seedTitle, body };
        }
      }
    }

    return null;
  });

  // Snapshot the current decoy entry once on mount so we can hydrate
  // title/body from its stored values and write edits back. useMemo
  // keeps the lookup pure (no ref reads/writes during render) and
  // re-runs only if the entryId actually changes.
  const decoyEntry = useMemo(() => {
    if (!entryId) return null;
    return loadDecoys().find((d) => d.id === entryId) || null;
  }, [entryId]);

  // Realism: freeze a copy of the ORIGINAL decoy content the moment
  // this note opens so `handleBack` can restore it when the spectator
  // exits. Only meaningful for `plain` entries (i.e. pre-configured
  // decoy notes tapped from the list) — compose (+) notes intentionally
  // persist. Reads STORAGE.DECOY_RESET_ON_EXIT lazily so the magician
  // can opt out from Settings. Captured only once via useState's lazy
  // init; subsequent renders never re-snapshot.
  const [originalDecoySnapshot] = useState(() => {
    if (entry !== "plain" || !entryId) return null;
    const optedOut = localStorage.getItem(STORAGE.DECOY_RESET_ON_EXIT) === "0";
    if (optedOut) return null;
    const row = loadDecoys().find((d) => d.id === entryId);
    if (!row) return null;
    return {
      title: typeof row.title === "string" ? row.title : "",
      body: typeof row.body === "string" ? row.body : "",
      preview: typeof row.preview === "string" ? row.preview : "",
    };
  });

  const [title, setTitle] = useState(() => {
    if (predictionSeed) return predictionSeed.title;
    const stored = decoyEntry && typeof decoyEntry.title === "string"
      ? decoyEntry.title
      : null;
    if (stored !== null && stored !== undefined) return stored;
    return entryTitle !== null ? entryTitle : getDefaultTitle();
  });
  const [body, setBody] = useState(() => {
    if (predictionSeed) return predictionSeed.body;
    // Trident always starts blank — persisted spectator words from a
    // previous performance must never bleed in. Sticky persistence
    // is intentionally skipped for this routine.
    if (entry === "trident") return "";
    const stored = decoyEntry && typeof decoyEntry.body === "string"
      ? decoyEntry.body
      : null;
    if (stored !== null && stored !== undefined && stored !== "") return stored;
    // Fallback to the legacy global scratch slot for compose/plain
    // entries that don't yet have a decoy id (older saved states).
    return localStorage.getItem(STORAGE.NOTE_BODY) || "";
  });
  const bodyRef = useRef(null);
  // Compute the effective "note date" as a real Date object once at
  // mount so both the visible header string AND the EXIF metadata we
  // stamp into shared images agree with each other. Falls through in
  // the same order the header does: Search-Force config > magician
  // entryTime > now-minus-backdate.
  const [effectiveDate] = useState(() => {
    if (entry === "searchforce") {
      const iso = localStorage.getItem(STORAGE.SEARCH_FORCE_DATE_ISO);
      if (iso) {
        const parsed = new Date(iso);
        if (!Number.isNaN(parsed.getTime())) return parsed;
      }
    }
    if (entryTime) {
      const parsed = new Date(entryTime);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date(Date.now() - backdateMin * 60 * 1000);
  });
  const [dateStr] = useState(() => {
    // Search-Force uses a fully custom date line ("Vanochtend om
    // 8:00 AM" by default) so the note reads as pre-written. Bypass
    // formatAppleDate entirely.
    if (entry === "searchforce" && entryDateLabel) {
      return entryDateLabel;
    }
    return formatAppleDate(effectiveDate, getLocale(langCode));
  });

  // Persist title/body. For decoys identified by id, the canonical
  // store is the decoy entry inside mr_decoy_notes (so the folder
  // list shows the latest preview line and the next open hydrates
  // from the same place). Trident & Swiper opt out — see comments
  // in the state-init blocks above.
  useEffect(() => {
    if (entry === "trident" || entry === "swiper" || entry === "searchforce") return;
    if (entryId) {
      const decoys = loadDecoys();
      const firstNonEmptyLine = (body || "").split("\n").find((l) => l.trim().length > 0) || "";
      // Only override the existing folder-list preview once the spectator
      // has actually typed something. An empty body keeps whatever preview
      // the magician set in Settings (e.g. "Milk, eggs, sourdough…") so
      // the folder list never loses its hand-curated look.
      const patch = { title, body };
      if (firstNonEmptyLine) patch.preview = firstNonEmptyLine.slice(0, 80);
      const next = updateDecoyById(decoys, entryId, patch);
      if (next !== decoys) saveDecoys(next);
    } else {
      // Legacy fallback: notes opened without an id still scratch
      // into the old global key so existing flows don't regress.
      localStorage.setItem(STORAGE.NOTE_BODY, body);
    }
  }, [body, title, entryId, entry]);

  // Auto-pop the on-screen keyboard the moment a note opens, matching
  // real Apple Notes. For brand-new compose notes (empty title +
  // empty body) we focus the TITLE so the spectator can start with
  // the heading; otherwise we focus the BODY at the end of any
  // existing text so the cursor lands where the next character goes.
  // Swiper notes are static predictions — never focus.
  //
  // iOS Safari ONLY opens the virtual keyboard when focus() runs
  // inside the user-gesture window that triggered the navigation.
  // useEffect + setTimeout fires too late (after the gesture has
  // expired) and the keyboard stays hidden — even though the input
  // is technically focused. The fix is a two-step relay:
  //   1. DecoyNotes.openRow / openCompose synchronously focuses a
  //      persistent "keyboard bridge" <input> mounted at the App
  //      root BEFORE calling navigate(). iOS sees this as a real
  //      input-focus inside the touchend handler and starts the
  //      keyboard-show animation.
  //   2. Below: useLayoutEffect transfers focus to the real title/
  //      body input synchronously on mount. Because the keyboard
  //      animation is still in flight, iOS attaches it to the new
  //      element instead of dismissing it.
  // The 60ms setTimeout previously here defeated step 2 — replaced
  // with a synchronous layout effect.
  const titleRef = useRef(null);
  const autoFocusSkip = entry === "swiper" || entry === "searchforce";
  useLayoutEffect(() => {
    if (autoFocusSkip) return;
    const startInTitle = (!title || title.trim() === "") && (!body || body === "");
    const el = startInTitle ? titleRef.current : bodyRef.current;
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
      if (el === bodyRef.current && typeof el.setSelectionRange === "function") {
        const end = (body || "").length;
        el.setSelectionRange(end, end);
      }
    } catch { /* iOS Safari may still reject pre-paint focus — best effort */ }
    // Intentionally only run once on mount — focusing again on
    // every keystroke would fight the user's caret position.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Drawing layer (RETIRED) ---
  // The pencil icon in the toolbar is now repurposed as the silent
  // arming toggle for the Add-a-Number routine. The canvas, vision
  // pipeline and clearDrawing handler have been removed. We still pass
  // a null canvasRef into useReveal for backwards compatibility — the
  // hook short-circuits its draw branch when mode === "type".
  const isStaticPrediction = entry === "swiper" || entry === "searchforce";
  const canvasRef = useRef(null);

  // --- Add-a-Number arming ---
  // Snapshot the config once when entering the screen — re-reading
  // mid-performance would create live-edit risk. useState's lazy
  // initializer gives us a stable snapshot without touching refs
  // during render.
  const [addnumConfig] = useState(() => getAddNumberConfig());
  const [addnumArmed, setAddnumArmed] = useState(false);
  const addnumSwappedRef = useRef(false);
  // Re-derived view of the body parsed as three numbered lines.
  const addnumNumsRef = useRef({ 1: null, 2: null, 3: null });

  // --- Trident — second decoy "type words in any order on lines 1/2/3" ---
  // We capture the chronological order the spectator first writes a
  // non-empty word on each line. Subsequent edits or backspaces never
  // change the order (first touch wins). The reveal text is computed on
  // demand when the ••• button is tapped and displayed via the same
  // peek/scroll/whisper plumbing the rest of the app uses.
  const isTrident = entry === "trident";
  const tridentConfig = useRef(getTridentConfig()).current;
  const tridentOrderRef = useRef([]); // chronological list of line numbers 1..3
  const [tridentReveal, setTridentReveal] = useState("");

  // --- Prediction / Scroll-peek typewriter ---
  const typewriter = useTypewriter(setBody);

  const composeTypedReveal = useCallback((answerText) => {
    const a = (answerText || "").trim();
    if (!a) return "";
    const p = (predictionPrefix || "").trim();
    return p ? `${p} ${a}` : a;
  }, [predictionPrefix]);

  /** Scroll-peek: append the prefix+answer far below the existing body so
   * the magician has to scroll down to see it. */
  const writeScrollPeek = useCallback((answerText) => {
    const payload = composeTypedReveal(answerText);
    if (!payload) return;
    const padding = "\n".repeat(SCROLL_PEEK_PADDING_LINES);
    typewriter.run(padding + payload, { append: true });
    requestAnimationFrame(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    });
  }, [composeTypedReveal, typewriter]);

  // --- Long-press reveal + triple-tap exit ---
  const reveal = useReveal({
    mode: "type",
    revealStyle,
    body,
    canvasRef,
    multiQuestion,
    onScrollAppend: useCallback((value) => writeScrollPeek(value), [writeScrollPeek]),
    onExit: useCallback(() => {
      typewriter.cancel();
      navigate("/");
    }, [typewriter, navigate]),
  });

  // --- Whisper reveal: speak the answer through the magician's earbud
  // whenever a new revealValue lands in whisper mode.
  const lastWhisperedRef = useRef("");
  useEffect(() => {
    if (revealStyle !== "whisper") return;
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const value = isTrident ? tridentReveal : reveal.revealValue;
    if (!value || value === "—" || value === lastWhisperedRef.current) return;
    lastWhisperedRef.current = value;
    try {
      window.speechSynthesis.cancel();
      const u = new window.SpeechSynthesisUtterance(value);
      const browserLang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
      u.lang = langCode && langCode !== "auto" ? langCode : browserLang;
      u.rate = 0.95;
      u.volume = 1.0;
      u.pitch = 1.0;
      window.speechSynthesis.speak(u);
    } catch {
      /* speechSynthesis not available — silently no-op */
    }
  }, [reveal.revealValue, tridentReveal, isTrident, revealStyle, langCode]);

  // --- Scroll-peek dispatch for Trident: write the chronological reveal
  // into the body far below the fold so the magician scrolls down to it
  // exactly like the compose multi-question peek. */
  const lastTridentScrollRef = useRef("");
  useEffect(() => {
    if (!isTrident) return;
    if (revealStyle !== "scroll") return;
    if (!tridentReveal || tridentReveal === lastTridentScrollRef.current) return;
    lastTridentScrollRef.current = tridentReveal;
    writeScrollPeek(tridentReveal);
  }, [isTrident, revealStyle, tridentReveal, writeScrollPeek]);

  // --- Consume the prediction seed transients once after first paint ---
  // NOTE: We deliberately do NOT clear the swiper card here anymore.
  // The magician now controls the swiper lifetime — the value stays
  // armed across trick open/close cycles so re-opening the note during
  // the same performance still reveals the encoded card. It only gets
  // wiped when the magician triple-taps back to Setup (see
  // `onHeaderTap` in DecoyNotes.jsx) or when Apocalypse consumes it.
  const predictionConsumedRef = useRef(false);
  useEffect(() => {
    if (predictionConsumedRef.current) return;
    if (!predictionSeed) return;
    predictionConsumedRef.current = true;
  }, [predictionSeed]);

  // --- Add-a-Number: parse body, fire trigger when armed ---------------
  // Re-parse on every body change; arming and trigger are gated below.
  useEffect(() => {
    addnumNumsRef.current = parseNumberedLines(body);
  }, [body]);

  /** Run the configured swap exactly once per arming session. */
  const performAddnumSwap = useCallback(() => {
    if (!addnumArmed) return;
    if (addnumSwappedRef.current) return;
    const nums = addnumNumsRef.current;
    if (!isAddNumberReady(nums)) return;
    const { updates } = computeAddNumberSwap(nums, addnumConfig);
    const positions = Object.keys(updates);
    if (positions.length === 0) return; // silent abort: impossible force
    addnumSwappedRef.current = true;
    setBody((prev) => {
      let next = prev;
      for (const pos of positions) {
        const r = replaceNumberOnLine(next, parseInt(pos, 10), updates[pos]);
        if (r.replaced) next = r.text;
      }
      return next;
    });
  }, [addnumArmed, addnumConfig]);

  // Motion-trigger listener — attached only while armed AND the
  // magician chose a motion-based trigger. iOS permission was requested
  // inside the pencil-tap handler below (the only user-gesture context
  // Safari accepts).
  useEffect(() => {
    if (!addnumArmed) return;
    if (addnumConfig.trigger !== "handoff" && addnumConfig.trigger !== "placedown") return;

    const restState = { sawMotion: false, restStartedAt: 0 };
    const onMotion = (e) => {
      if (addnumSwappedRef.current) return;
      if (!isAddNumberReady(addnumNumsRef.current)) return;
      const a = e.accelerationIncludingGravity || e.acceleration;
      if (!a) return;
      const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2);

      if (addnumConfig.trigger === "handoff") {
        if (mag > 14) performAddnumSwap();
        return;
      }
      // placedown: wait for pickup → rest transition
      const restingBand = mag > 8.5 && mag < 11.0;
      if (!restingBand) {
        restState.sawMotion = true;
        restState.restStartedAt = 0;
        return;
      }
      if (!restState.sawMotion) return;
      if (restState.restStartedAt === 0) {
        restState.restStartedAt = Date.now();
        return;
      }
      if (Date.now() - restState.restStartedAt >= 600) {
        performAddnumSwap();
      }
    };
    window.addEventListener("devicemotion", onMotion);
    return () => window.removeEventListener("devicemotion", onMotion);
  }, [addnumArmed, addnumConfig.trigger, performAddnumSwap]);

  /** Body blur handler — wired only when `done` trigger is active and
   *  arming is on. iOS dismisses the keyboard via Done or by tapping
   *  outside; both fire blur. */
  const onBodyBlur = useCallback(() => {
    if (!addnumArmed) return;
    if (addnumConfig.trigger !== "done") return;
    performAddnumSwap();
  }, [addnumArmed, addnumConfig.trigger, performAddnumSwap]);

  const onBodyKeyDown = useCallback(
    (e) => {
      if (!addnumArmed) return;
      if (addnumConfig.trigger !== "done") return;
      if (e.key === "Enter" && !e.shiftKey) {
        setTimeout(performAddnumSwap, 0);
      }
    },
    [addnumArmed, addnumConfig.trigger, performAddnumSwap],
  );

  /** Body change handler — passes through to setBody and, on Trident
   *  entries, captures the first-touch chronological order of the three
   *  numbered lines. */
  const onBodyChange = useCallback(
    (e) => {
      const next = e.target.value;
      setBody(next);
      if (!isTrident) return;
      const words = parseTridentWordLines(next);
      const order = tridentOrderRef.current;
      for (const pos of [1, 2, 3]) {
        if (words[pos] && !order.includes(pos)) {
          order.push(pos);
        }
      }
    },
    [isTrident],
  );

  /** Tap on the ••• reveal button. For Trident notes we compute the
   *  reveal locally (no AI / no useReveal call); the result is rendered
   *  in the date-line peek slot and, for whisper mode, also spoken. */
  const handleRevealTap = useCallback(() => {
    if (isTrident) {
      const words = parseTridentWordLines(body);
      const order = tridentOrderRef.current;
      // Lines that have content but weren't tracked yet (e.g. seed). Add them.
      for (const pos of [1, 2, 3]) {
        if (words[pos] && !order.includes(pos)) order.push(pos);
      }
      const orderedWords = order.map((pos) => words[pos] || "");
      const text = buildTridentReveal(tridentConfig.template, orderedWords);
      setTridentReveal(text);
      return;
    }
    reveal.triggerReveal();
  }, [isTrident, body, tridentConfig.template, reveal]);

  /** Secret pencil-tap. Flips the arming state and — on first arm of
   *  a motion-based trigger — requests iOS DeviceMotion permission
   *  inside this synchronous gesture so Safari accepts it. */
  const handlePencilTap = useCallback(() => {
    setAddnumArmed((prev) => {
      const next = !prev;
      if (next) {
        addnumSwappedRef.current = false;
        if (addnumConfig.trigger === "handoff" || addnumConfig.trigger === "placedown") {
          const E = typeof window !== "undefined" ? window.DeviceMotionEvent : null;
          if (E && typeof E.requestPermission === "function") {
            try { E.requestPermission().catch(() => {}); } catch { /* ignore */ }
          }
        }
      }
      return next;
    });
  }, [addnumConfig.trigger]);

  /**
   * Back button — exits to the fake Notes folder. The swiper card is
   * intentionally NOT cleared here — the magician wants the armed
   * value to survive close/reopen cycles during a single performance.
   * The card is wiped only on the triple-tap escape in DecoyNotes
   * (route to /setup) or when Apocalypse's `finish()` consumes it.
   */
  const handleBack = useCallback(() => {
    hapticTap();
    try { window.speechSynthesis?.cancel?.(); } catch { /* ignore */ }
    canvasRef.current?.clear?.();
    typewriter.cancel();
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText("").catch(() => {});
    }
    // Realism: restore the pre-open snapshot for plain decoys so any
    // spectator edits made during this session disappear on exit.
    // Runs BEFORE the sticky-input teardown below so the restored row
    // wins over the effect-driven persist that may fire once more on
    // unmount. Compose / swiper / trident / searchforce intentionally
    // skip this — their state is owned by the trick, not the row.
    if (originalDecoySnapshot && entry === "plain" && entryId) {
      const decoys = loadDecoys();
      const next = updateDecoyById(decoys, entryId, {
        title: originalDecoySnapshot.title,
        body: originalDecoySnapshot.body,
        preview: originalDecoySnapshot.preview,
      });
      if (next !== decoys) saveDecoys(next);
    }
    // Sticky-input notes own their state inside the decoy row itself
    // (persisted via the useEffect above). Clearing the legacy global
    // scratch keys + resetting local state would fire the persist
    // effect ONE more time with empty values and silently wipe the
    // spectator's typed title/body. So we only do the legacy teardown
    // for entries without a stable decoy id.
    if (!entryId) {
      localStorage.removeItem(STORAGE.NOTE_TITLE);
      localStorage.removeItem(STORAGE.NOTE_BODY);
      localStorage.removeItem(STORAGE.NOTE_DRAWING);
      setTitle(getDefaultTitle());
      setBody("");
    }
    // Preserve the search bar state when returning to the notes
    // list — matches iOS-native Notes behaviour where "< Notes" from
    // a search-result row lands you back inside the same active
    // search. `returnSearch` is set by DecoyNotes when it opened this
    // note; we just forward it back.
    const returnSearch = (location.state && location.state.returnSearch) || "";
    navigate("/notes", { state: { returnSearch } });
  }, [typewriter, navigate, entryId, entry, originalDecoySnapshot, location.state]);

  // Localized UI strings + RTL detection based on the configured language
  const tr = useCallback((key) => t(key, langCode), [langCode]);
  const isRTL = /^(?:ar|he|fa|ur)/.test(langCode || "");

  return (
    <div
      className="fixed inset-0 w-screen h-screen bg-white text-black flex flex-col"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      dir={isRTL ? "rtl" : "ltr"}
      onContextMenu={(e) => e.preventDefault()}
      data-testid="trick-page"
    >
      {/* Top toolbar */}
      <div className="relative flex items-center justify-between px-3 pt-3 pb-2 border-b border-neutral-100 select-none">
        <button
          type="button"
          onClick={handleBack}
          aria-label="back to notes"
          className="relative z-40 flex items-center text-[17px] text-[#FFB400] font-normal bg-transparent border-0 p-0 cursor-pointer active:opacity-70"
          data-testid="back-button"
        >
          <svg width="13" height="22" viewBox="0 0 13 22" fill="none" aria-hidden="true">
            <path d="M11 1L2 11L11 21" stroke="#FFB400" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="ml-1.5">{tr("notes")}</span>
        </button>

        <div className="flex items-center gap-4 text-[#FFB400]">
          {/* Native share — opens the real iOS Share Sheet (which
              includes AirDrop) so the spectator can transfer the note
              to their own device as a souvenir. Always visible so it
              never doubles as a "special note" tell. */}
          <button
            type="button"
            onClick={async () => {
              hapticTap();
              try {
                const result = await shareNoteAsAirDrop({
                  title,
                  date: dateStr,
                  body,
                  // Backdate the shared JPEG's EXIF metadata (DateTime,
                  // DateTimeOriginal, DateTimeDigitized) so the "Info"
                  // panel on the spectator's iPhone after long-press
                  // reads the SAME date the note claims to be from —
                  // otherwise Photos would give the trick away with a
                  // fresh "Today" timestamp.
                  photoDate: effectiveDate,
                });
                if (result.aborted) return; // user dismissed sheet
                if (result.ok) {
                  const msg = result.mode === "clipboard"
                    ? tr("copied_to_clipboard") || "Copied to clipboard"
                    : "";
                  if (msg) toast.success(msg);
                } else {
                  toast.error(tr("share_failed") || "Share unavailable");
                }
              } catch { /* swallow — user already saw sheet */ }
            }}
            aria-label="share"
            className="p-1 rounded-md bg-transparent border-0"
            data-testid="share-button"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 3v13" stroke="#FFB400" strokeWidth="1.7" strokeLinecap="round"/>
              <path d="M8 7l4-4 4 4" stroke="#FFB400" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" stroke="#FFB400" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Secret Add-a-Number arming button (disguised as Apple Notes
              markup/pencil). A subtle background tint when armed mirrors
              real Notes' "markup on" state, so it's invisible as a tell.
              Hidden on swiper notes where the body is locked. */}
          {!isStaticPrediction && (
            <button
              type="button"
              onClick={handlePencilTap}
              aria-label="markup"
              className="p-1 rounded-md bg-transparent border-0"
              data-testid="addnum-arm-toggle"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M3 21l3.5-1L20 6.5 17.5 4 4 17.5 3 21z" stroke="#FFB400" strokeWidth="1.7" strokeLinejoin="round"/>
                <path d="M14.5 6.5l2.5 2.5" stroke="#FFB400" strokeWidth="1.7"/>
              </svg>
            </button>
          )}
          {/* Tap-to-reveal — only meaningful for compose/plain entries. */}
          {!isStaticPrediction && (
            <button
              type="button"
              onClick={handleRevealTap}
              aria-label="more"
              className="p-1 rounded-md bg-transparent border-0"
              data-testid="reveal-trigger"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="6"  r="1.6" fill="#FFB400"/>
                <circle cx="12" cy="12" r="1.6" fill="#FFB400"/>
                <circle cx="12" cy="18" r="1.6" fill="#FFB400"/>
              </svg>
            </button>
          )}
        </div>

        {/* Indicator dots removed at user request — neither the amber
            "thinking" dot nor the green "ready" dot are shown anymore,
            to keep the editor visually identical to native Notes. */}
      </div>

      {/* Title + Date (always visible) */}
      <div className="px-5 pt-5 pb-2 shrink-0">
        <input
          type="text"
          ref={titleRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={tr("title")}
          className="w-full text-[26px] font-bold leading-tight text-black placeholder:text-neutral-300 outline-none bg-transparent"
          data-testid="note-title"
          autoCorrect="on"
          autoCapitalize="sentences"
          spellCheck={true}
        />
        <div
          className="mt-1.5 text-[12.5px] text-neutral-400 select-none inline-block"
          onClick={isStaticPrediction ? undefined : (isTrident ? handleRevealTap : reveal.handleDateTap)}
          onMouseDown={isStaticPrediction ? undefined : (isTrident ? undefined : reveal.startPress)}
          onMouseUp={isStaticPrediction ? undefined : (isTrident ? undefined : reveal.endPress)}
          onMouseLeave={isStaticPrediction ? undefined : (isTrident ? undefined : reveal.endPress)}
          onTouchStart={isStaticPrediction ? undefined : (isTrident ? undefined : (e) => { e.stopPropagation(); reveal.startPress(); })}
          onTouchEnd={isStaticPrediction ? undefined : (isTrident ? undefined : (e) => { e.stopPropagation(); reveal.endPress(); })}
          onTouchCancel={isStaticPrediction ? undefined : (isTrident ? undefined : reveal.endPress)}
          data-testid="note-date"
        >
          {!isStaticPrediction && (
            isTrident
              ? (revealStyle === "peek" && tridentReveal)
              : (revealStyle !== "whisper" && reveal.revealValue && reveal.revealValue !== "—" ? reveal.revealValue : "")
          ) ? (
            <span data-testid="subliminal-answer">
              {compactRevealOn
                ? abbreviateReveal(isTrident ? tridentReveal : reveal.revealValue)
                : (isTrident ? tridentReveal : reveal.revealValue)}
            </span>
          ) : (
            <span data-testid="note-date-text">{dateStr}</span>
          )}
        </div>
      </div>

      {/* Body — single textarea. Drawing mode was retired in Feb 2026;
          the pencil button is now the silent Add-a-Number arming toggle. */}
      <div className="flex-1 relative overflow-hidden">
        <div className="absolute inset-0 overflow-y-auto px-5 pt-2 pb-4">
          <textarea
            ref={bodyRef}
            value={body}
            onChange={onBodyChange}
            onBlur={onBodyBlur}
            onKeyDown={onBodyKeyDown}
            placeholder={tr("body")}
            className="w-full min-h-[80%] text-[17px] leading-[1.45] text-black placeholder:text-neutral-300 outline-none resize-none bg-transparent"
            data-testid="note-body"
            autoCorrect="on"
            autoCapitalize="sentences"
            spellCheck={true}
          />
        </div>
      </div>
    </div>
  );
}

function formatAppleDate(d, locale) {
  const date = d.toLocaleDateString(locale, {
    day: "numeric", month: "long", year: "numeric",
  });
  const time = d.toLocaleTimeString(locale, {
    hour: "2-digit", minute: "2-digit",
  });
  return `${date} ${time}`;
}
